import React, {
    ReactElement, ReactNode, useCallback, useState,
} from "react";
import { useDropzone } from "react-dropzone";
import { AiOutlineCloseCircle, AiOutlineEdit } from "react-icons/ai";
import { RiDeleteBinLine, RiUploadCloud2Line } from "@remixicon/react";
import classNames from "classnames";
import { useCreateUploadTokenMutation } from "../../../__generated__/api";
import { LoadingIcon } from "../../loading/Loading";
import Styles from "./IconUploader.module.scss";
import { useTheme } from "../../../contexts/Theme";
import Button from "../../theme/button/Button";

export interface IconUploaderProps {
    value: string | undefined;
    onChange: (url: string | undefined, mimeType: string | undefined, size: number | undefined, filename: string) => void;
    shouldStore?: boolean;
    defaultIcon?: ReactElement;
    isReadOnly?: boolean;
    shouldShowBorder?: boolean;
    className?: string;
    placeholder?: ReactNode;
    size?: "sm" | "md" | "lg" | "xl" | "xxl";
    imageClassName?: string;
    wrapperClassName?: string;
    buttonsClassName?: string;
    dropClassName?: string;
    isSquare?: boolean
}

export default function IconUploader({
    value, onChange, shouldStore, defaultIcon, shouldShowBorder,
    isReadOnly, className, placeholder, size, imageClassName,
    wrapperClassName, buttonsClassName, dropClassName, isSquare,
}: IconUploaderProps): ReactElement {
    const [uploadAttachment] = useCreateUploadTokenMutation();

    const theme = useTheme();

    const [isHoverApplicationIcon, setIsHoverApplicationIcon] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const onDrop = useCallback(async (acceptedFile: File[]) : Promise<undefined> => {
        setIsUploading(true);
        const file = acceptedFile[0];

        if (!file) {
            setIsUploading(false);
            return;
        }
        try {
            const meta = await uploadAttachment({
                variables: {
                    attachment: {
                        filename: file.name,
                        size: file.size,
                        mimeType: file.type,
                        shouldStore,
                    },
                },
            });

            if (!meta.data?.createUploadToken || meta.data.createUploadToken?.error) {
                return;
            }
            const { url } = meta.data.createUploadToken;

            const response = await fetch(url, {
                method: "POST",
                body: file,
                credentials: "include",
            });

            if (response.status !== 201) {
                return;
            }

            const json = await response.json() as {url : string};
            onChange(json.url, file.type, file.size, file.name);
        } finally {
            setIsUploading(false);
        }
    }, []);
    const {
        getRootProps, getInputProps, isDragActive, open,
    } = useDropzone({
        onDrop,
        multiple: false,
        maxFiles: 1,
        maxSize: 2e+7,
        accept: {
            "image/png": [".png"],
            "image/jpeg": [".jpeg", ".jpg"],
            "image/gif": [".gif"],
        },
    });

    let control: ReactElement;
    if (isUploading) {
        control = (
            <div className={`rounded p-2 ${shouldShowBorder ? "border" : ""}`}>
                <LoadingIcon size="100%" />
            </div>
        );
    } else if (isDragActive && !isReadOnly) {
        control = (
            <div
                className={theme.isV3
                    ? classNames(Styles["default-drop"], dropClassName)
                    : "text-light h-100 alert alert-info py-5 w-100 text-center border rounded"}
            >
                Drag and Drop files here
            </div>
        );
    } else if (!value) {
        control = (
            <>
                {defaultIcon ? (
                    <div className="position-relative">
                        {defaultIcon}
                        {isHoverApplicationIcon && (
                            <button
                                className="btn btn-blue-dark btn-sm fs-6 text-white
                                            position-absolute translate-middle top-50 start-50 opacity-50"
                                type="button"
                                aria-label="Edit the image source"
                            >
                                <AiOutlineEdit />
                            </button>
                        )}
                    </div>
                ) : (
                    <div className={theme.isV3
                        ? classNames(Styles["default-drop"], dropClassName)
                        : classNames(
                            "text-light h-100 py-5 d-flex",
                            "flew-grow-1 text-center rounded",
                            shouldShowBorder ? "border" : "",
                        // eslint-disable-next-line react/jsx-indent
                        )}
                    >
                        {placeholder}
                    </div>
                )}
                <input {...getInputProps()} />
            </>
        );
    } else {
        control = (
            <div className={`position-relative rounded border-light ${
                Styles[`logo-img-${size}`]} ${
                Styles[`logo-img-container`]} ${
                isReadOnly ? Styles["logo-img-read-only"] : ""
            }`}
            >

                <img
                    src={value}
                    alt="Company logo"
                    className={classNames(
                        theme.isV3 ? Styles["logo-img-value"] : `rounded position-relative img-thumbnail`,
                        imageClassName,
                        Styles[`base-logo-img`],
                        Styles[`logo-img-${size}`],
                        shouldShowBorder ? "" : "border-0",
                    )}
                />
                {isHoverApplicationIcon && (
                    <>
                        <button
                            className={`btn btn-blue-dark btn-sm  text-white`
                                        + ` position-absolute bottom-0 start-0 opacity-50 ${
                                            size === "sm" ? "px-2 py-1 fs-6" : "fs-6"}`}
                            type="button"
                            onClick={evt => {
                                evt.stopPropagation();
                                onChange(undefined, undefined, undefined, "");
                            }}
                            aria-label="Remove the image"
                        >
                            <AiOutlineCloseCircle />
                        </button>
                        <button
                            className={`btn btn-blue-dark btn-sm  text-white`
                                        + ` position-absolute top-0 start-0 opacity-50 ${
                                            size === "sm" ? "px-2 py-1 fs-6" : "fs-6"}`}
                            type="button"
                            aria-label="Edit the image source"
                        >
                            <AiOutlineEdit />
                        </button>
                    </>
                )}

            </div>
        );
    }

    if (isReadOnly) {
        return (
            <div
                className={`rounded text-center ${wrapperClassName} ${
                    Styles[value ? `logo-img-${size}` : "logo-empty"]}`}
            >
                {control}
            </div>
        );
    }

    return (

        <div
            className={classNames(
                theme.isV3 ? Styles["logo-img"] : `mb-2 rounded`,
                wrapperClassName,
                Styles[(value || isUploading) ? `logo-img-wrapper-${size}` : "logo-empty"],
                className,
            )}
            onMouseEnter={!theme.isV3 && !isReadOnly ? () => setIsHoverApplicationIcon(true) : undefined}
            onMouseLeave={!theme.isV3 && !isReadOnly ? () => setIsHoverApplicationIcon(false) : undefined}
            {...getRootProps()}
        >
            {control}

            {theme.isV3 && (
                <div className={classNames(Styles.buttons, buttonsClassName, isSquare ? Styles["is-square"] : "")}>
                    <Button
                        variant={isSquare ? "secondary" : "outline-gray"}
                        onClick={open}
                        size="small"
                        prefixIcon={<RiUploadCloud2Line />}
                    />
                    <Button
                        variant={isSquare ? "secondary" : "outline-gray"}
                        onClick={evt => {
                            evt.stopPropagation();
                            onChange(undefined, undefined, undefined, "");
                        }}
                        size="small"
                        prefixIcon={<RiDeleteBinLine />}
                    />
                </div>
            )}
        </div>

    );
}

IconUploader.defaultProps = {
    shouldStore: false,
    defaultIcon: undefined,
    shouldShowBorder: true,
    isReadOnly: false,
    className: "",
    placeholder: "Click or Drag to Upload an Image",
    size: "lg",
    imageClassName: "",
    wrapperClassName: "",
    buttonsClassName: "",
    dropClassName: "",
    isSquare: false,
};
